/**
 * Shared display helpers for the Finance screens: enum labels, currency
 * formatting (EUR reporting currency + native), last-transaction recency
 * hints (>30d amber, >60d red — design §10), and API error helpers shared
 * by all Finance screens so FIN-10..13 can reuse without copy-paste.
 */

import type {
  FinanceAccountType,
  FinanceAccountGroup,
  FinanceCategoryKind,
} from '@k-os/core';
import type { FinanceCategoryDto } from '../../api/finance';
import { ApiError } from '../../api/client';

export const ACCOUNT_TYPE_LABELS: Record<FinanceAccountType, string> = {
  checking: 'Checking',
  savings: 'Savings',
  'credit-card': 'Credit card',
  investment: 'Investment',
  loan: 'Loan',
  cash: 'Cash',
};

export const ACCOUNT_GROUP_LABELS: Record<FinanceAccountGroup, string> = {
  asset: 'Asset',
  liability: 'Liability',
};

export const CATEGORY_KIND_LABELS: Record<FinanceCategoryKind, string> = {
  income: 'Income',
  expense: 'Expense',
};

const eurFormatter = new Intl.NumberFormat('en-IE', {
  style: 'currency',
  currency: 'EUR',
});

export interface AccountLite {
  id: string;
  name: string;
  parentAccountId: string | null;
}

/**
 * Valid move targets for a transaction currently in `accountId`: the account's
 * parent (move back up) and its direct children (move down). Sub-account model,
 * one level — siblings are reached via two moves.
 */
export function accountMoveTargets<T extends AccountLite>(accounts: T[], accountId: string): T[] {
  const acc = accounts.find((a) => a.id === accountId);
  if (!acc) return [];
  const targets: T[] = [];
  if (acc.parentAccountId) {
    const parent = accounts.find((a) => a.id === acc.parentAccountId);
    if (parent) targets.push(parent);
  }
  for (const a of accounts) if (a.parentAccountId === accountId) targets.push(a);
  return targets;
}

export function formatEur(amount: number): string {
  return eurFormatter.format(amount);
}

export function formatNative(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(amount);
  } catch {
    // Unknown / invalid ISO code — fall back to a plain number + code.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export type StaleLevel = 'none' | 'fresh' | 'warn' | 'danger';

export interface StaleHint {
  level: StaleLevel;
  label: string;
}

// ─── API error helpers ────────────────────────────────────────────────────────

/**
 * Extract the `error` code string from an ApiError body, if present.
 * Returns null for any other error type or if the body lacks an `error` field.
 */
export function financeErrorCode(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: unknown }).error;
    return typeof e === 'string' ? e : null;
  }
  return null;
}

/**
 * Human-readable error message for display in Finance screen error slots.
 * Prefers the server-supplied `error` code (underscores → spaces), falls
 * back to the JS Error message, then a generic fallback string.
 */
export function financeErrorMessage(err: unknown): string | null {
  if (!err) return null;
  const code = financeErrorCode(err);
  if (code) return code.replace(/_/g, ' ');
  return (err as Error)?.message ?? 'Something went wrong.';
}

// ─── Category option flattening (shared by Settings / Transactions / Import) ──

export interface CategoryOption {
  id: string;
  /** Indented display label reflecting tree depth. */
  label: string;
  kind: FinanceCategoryKind;
  /** Tree depth, 0-based, for callers that want their own indentation. */
  depth: number;
}

interface FlatCategory {
  id: string;
  name: string;
  parentId: string | null;
  kind: FinanceCategoryKind;
}

/**
 * Flatten a flat category list into depth-ordered, indented options
 * (income roots before expense, alphabetical within a level). Parents come
 * before their children so the result reads as a collapsed tree.
 */
export function buildCategoryOptions(flat: FlatCategory[]): CategoryOption[] {
  const childrenOf = new Map<string | null, FlatCategory[]>();
  for (const c of flat) {
    const key = c.parentId ?? null;
    const arr = childrenOf.get(key) ?? [];
    arr.push(c);
    childrenOf.set(key, arr);
  }
  for (const arr of childrenOf.values()) {
    arr.sort((a, b) => {
      if (a.parentId === null && a.kind !== b.kind) {
        return a.kind === 'income' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  const out: CategoryOption[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const c of childrenOf.get(parentId) ?? []) {
      out.push({ id: c.id, label: `${'  '.repeat(depth)}${c.name}`, kind: c.kind, depth });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// ─── Category tree builder (shared by Categories + Budgets screens) ──────────

export interface CategoryTreeNode {
  cat: FinanceCategoryDto;
  depth: number;
  children: CategoryTreeNode[];
}

export interface BuildCategoryTreeOpts {
  /**
   * When set, only include categories of this kind.
   * Omit (or pass undefined) to include both income and expense.
   */
  kind?: FinanceCategoryKind;
  /**
   * When true, exclude archived categories (archivedAt !== null).
   * Default: false (include archived).
   */
  excludeArchived?: boolean;
}

/**
 * Build a depth-labelled, alphabetically sorted tree from a flat
 * FinanceCategoryDto list.
 *
 * Filtering behaviour:
 *   - `kind`            — keep only categories of this kind (default: all)
 *   - `excludeArchived` — drop categories with archivedAt set (default: false)
 *
 * Sorting: at depth 1 income roots sort before expense roots; within a
 * level nodes sort alphabetically by name.  This matches the historical
 * behaviour of both the Categories and Budgets local builders.
 */
export function buildCategoryTree(
  flat: FinanceCategoryDto[],
  opts: BuildCategoryTreeOpts = {},
): CategoryTreeNode[] {
  const { kind, excludeArchived = false } = opts;

  const filtered = flat.filter((c) => {
    if (kind !== undefined && c.kind !== kind) return false;
    if (excludeArchived && c.archivedAt !== null) return false;
    return true;
  });

  const byId = new Map<string, CategoryTreeNode>();
  for (const cat of filtered) byId.set(cat.id, { cat, depth: 1, children: [] });

  const roots: CategoryTreeNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.cat.parentId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  // Assign depth top-down (a parent removed by the filter — e.g. inactive and
  // hidden — orphans its children, which then surface as roots).
  const assignDepth = (nodes: CategoryTreeNode[], depth: number) => {
    for (const n of nodes) {
      n.depth = depth;
      assignDepth(n.children, depth + 1);
    }
  };
  assignDepth(roots, 1);

  const sortRec = (nodes: CategoryTreeNode[]) => {
    nodes.sort((a, b) => {
      // At depth 1 income roots sort before expense roots.
      if (a.depth === 1 && a.cat.kind !== b.cat.kind) {
        return a.cat.kind === 'income' ? -1 : 1;
      }
      return a.cat.name.localeCompare(b.cat.name);
    });
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** Days since the last transaction, with a level for color hinting. */
export function staleHint(lastTransactionDate: string | null): StaleHint {
  if (!lastTransactionDate) return { level: 'none', label: 'No movement yet' };
  const last = new Date(`${lastTransactionDate}T00:00:00`);
  const days = Math.floor((Date.now() - last.getTime()) / 86_400_000);
  const label =
    days <= 0
      ? 'Today'
      : days === 1
        ? 'Yesterday'
        : days < 30
          ? `${days}d ago`
          : days < 60
            ? `${days}d ago`
            : days < 365
              ? `${Math.round(days / 30)}mo ago`
              : `${Math.round(days / 365)}y ago`;
  const level: StaleLevel = days > 60 ? 'danger' : days > 30 ? 'warn' : 'fresh';
  return { level, label };
}
